import {
  useCallback,
  useEffect,
  useMemo,
  MouseEvent as ReactMouseEvent,
} from 'react';
import classNames from 'classnames';
import '../../Components/Common/typography.css';
import { ReactComponent as SortDescIcon } from '../../assets/icons/arrow-down-s-fill.svg';
import { ReactComponent as SortableIcon } from '../../assets/icons/arrow-down-s-unfill.svg';
import { ReactComponent as SortAscIcon } from '../../assets/icons/arrow-up-s-fill.svg';
import { getFlagSvg } from '../../Components/Common/Flag/Flag';
import {
  FundCriteria,
  FundScreenerDataType,
  FundScreenerSortingType,
} from '../../types/fundScreener.type';
import { ScreenerTableColumnType } from '../../types/screener.type';
import { CommonTooltipType } from '../../types/common.type';
import {
  formatLocaleNumber,
  formatMillions,
  formatPercent,
} from '../../helpers/number';
import { FC_GROUPS } from '../../constants/fundScreener/fundCriteria.constant';

interface UseFundScreenerColumnProps {
  selectedCriteria: FundCriteria[];
  market: string;
  page: number;
  size: number;
  isSmallScreen?: boolean;
  sorting: FundScreenerSortingType;
  setSorting: (sorting: FundScreenerSortingType) => void;
  setTooltip: (
    tooltip:
      | CommonTooltipType
      | ((prev: CommonTooltipType) => CommonTooltipType),
  ) => void;
  handleFundClick: (ric: string) => void;
}

// NAV decimals by market: US quotes to 4 decimals, AU (and everything else) to 3.
const getNavDecimals = (market: string) => (market === 'US' ? 4 : 3);

const ArrowSortingIndicator = ({ sorting }: { sorting: string }) => {
  const Icon =
    sorting === 'asc' ? SortAscIcon : sorting ? SortDescIcon : SortableIcon;
  return (
    <div className="fund-screener-table-sorting">
      <Icon
        className={`fund-screener-table-sorting-icon${
          sorting ? ' active fund-screener-table-sorting-icon--fill' : ''
        }`}
      />
    </div>
  );
};

const ValueWithCurrency = ({
  value,
  currency,
  decimals,
  formatValue = formatLocaleNumber,
}: {
  value: number | null;
  currency: string;
  decimals: number;
  formatValue?: (value: number | null, decimals: number) => string;
}) => (
  <div className="fund-screener-table-cell-value-container">
    <div className="fund-screener-table-cell-value body-m-regular">
      {formatValue(value, decimals)}
    </div>
    <div className="fund-screener-table-cell-currency body-s-regular">
      {currency}
    </div>
  </div>
);

// Wraps single-line values in NAV/TNA's two-line container (with an empty
// hidden second line) so they land on the same baseline, not dead-center.
const SingleLineValue = ({
  className,
  containerClassName,
  children,
}: {
  className?: string;
  containerClassName?: string;
  children: React.ReactNode;
}) => (
  <div
    className={classNames(
      'fund-screener-table-cell-value-container',
      containerClassName,
    )}
  >
    <div className={classNames('fund-screener-table-cell-value', className)}>
      {children}
    </div>
    <div
      className="fund-screener-table-cell-currency body-s-regular"
      aria-hidden="true"
    >
      &nbsp;
    </div>
  </div>
);

// Key measurements/Lipper score suffix their base label with the period,
// e.g. "Alpha" + "3y" -> "Alpha3Y". "Overall" has no suffix.
const PERIOD_SUFFIX: { [period: string]: string } = {
  '1m': '1M',
  '3m': '3M',
  '6m': '6M',
  '1y': '1Y',
  '3y': '3Y',
  '5y': '5Y',
  '10y': '10Y',
  Overall: '',
};

const getCriterionTableHeaderLabel = (criterion: FundCriteria) => {
  if (criterion.tableShortLabel) return criterion.tableShortLabel;
  if (criterion.tableShortLabelBase) {
    const suffix = PERIOD_SUFFIX[criterion.timeValue] ?? '';
    return `${criterion.tableShortLabelBase}${suffix}`;
  }
  return criterion.shortLabel || criterion.label;
};

const useFundScreenerColumn = ({
  selectedCriteria,
  market,
  page,
  size,
  isSmallScreen = false,
  sorting,
  setSorting,
  setTooltip,
  handleFundClick,
}: UseFundScreenerColumnProps) => {
  useEffect(() => {
    const handleScroll = () => setTooltip((prev) => ({ ...prev, show: false }));
    document.addEventListener('scroll', handleScroll, true);
    return () => document.removeEventListener('scroll', handleScroll, true);
  }, [setTooltip]);

  const handleSorting = useCallback(
    (id: string) => {
      setSorting({
        id,
        asc: sorting.id === id ? !sorting.asc : false,
      });
    },
    [sorting, setSorting],
  );

  const detectSorting = (id: string) =>
    sorting.id === id ? (sorting.asc ? 'asc' : 'desc') : '';

  // Header shows the short label; hovering reveals the full name in a tooltip.
  const handleHeaderMouseEnter = (
    e: ReactMouseEvent<HTMLDivElement>,
    longLabel: string,
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    setTooltip({
      content: longLabel,
      show: true,
      x: rect.left + scrollX,
      y: rect.bottom + 2 + scrollY,
    });
  };

  const handleHeaderMouseLeave = () => {
    setTooltip((prev) => ({ ...prev, show: false }));
  };

  const sortableHeader = (
    id: string,
    label: string,
    align: 'left' | 'right' = 'left',
    longLabel?: string,
  ) => (
    <div
      className={`fund-screener-table-header-wrapper ${align === 'right' ? 'align-right' : ''}`}
      onClick={() => handleSorting(id)}
    >
      <div
        className="fund-screener-table-header-title"
        onMouseEnter={
          longLabel && longLabel !== label
            ? (e) => handleHeaderMouseEnter(e, longLabel)
            : undefined
        }
        onMouseLeave={
          longLabel && longLabel !== label ? handleHeaderMouseLeave : undefined
        }
      >
        {label}
      </div>
      <ArrowSortingIndicator sorting={detectSorting(id)} />
    </div>
  );

  // Always shown regardless of selected criteria.
  const basicColumns: ScreenerTableColumnType[] = useMemo(() => {
    const indexColumn: ScreenerTableColumnType = {
      id: 'index',
      accessorKey: 'index',
      label: '#',
      cellClassName:
        'fund-screener-table-index fund-screener-table-index-sticky',
      headerClassName:
        'fund-screener-table-header-index fund-screener-table-header-index-sticky',
      renderHeader: () => '#',
      // Dedicated classes — reusing SingleLineValue's equal-specificity ones
      // let NAV/TNA's flex-end/text-primary silently win over these.
      renderCell: (_row: any, index: number) => (
        <div className="fund-screener-table-index-wrapper">
          <span className="fund-screener-table-index-number body-s-regular">
            {(page - 1) * size + index + 1}
          </span>
        </div>
      ),
    };

    const flagColumn: ScreenerTableColumnType = {
      id: 'flag',
      accessorKey: 'cc',
      label: '',
      // No width change on mobile, but the sticky `left` offset shifts once
      // the index column is gone (flag becomes the first frozen column).
      cellClassName: classNames(
        'fund-screener-table-flag',
        'fund-screener-table-flag-sticky',
        {
          'fund-screener-table-flag-mobile': isSmallScreen,
        },
      ),
      headerClassName: classNames(
        'fund-screener-table-header-flag',
        'fund-screener-table-header-flag-sticky',
        { 'fund-screener-table-header-flag-mobile': isSmallScreen },
      ),
      renderHeader: () => '',
      renderCell: (row: FundScreenerDataType) => (
        <span className="fund-screener-table-cell-flag">
          {getFlagSvg(row.cc)}
        </span>
      ),
    };

    const nameColumn: ScreenerTableColumnType = {
      id: 'name',
      accessorKey: 'name',
      label: 'Name',
      cellClassName: classNames(
        'fund-screener-table-name',
        'fund-screener-table-name-sticky',
        {
          'fund-screener-table-name-mobile': isSmallScreen,
        },
      ),
      headerClassName: classNames(
        'fund-screener-table-header-name',
        'fund-screener-table-header-name-sticky',
        { 'fund-screener-table-header-name-mobile': isSmallScreen },
      ),
      renderHeader: () => (
        <div className="fund-screener-table-header-wrapper fund-screener-table-header-name-code">
          <div
            className="fund-screener-table-header-sort-item"
            onClick={() => handleSorting('code')}
          >
            <div className="fund-screener-table-header-title">Code</div>
            <ArrowSortingIndicator sorting={detectSorting('code')} />
          </div>
          <div
            className="fund-screener-table-header-sort-item"
            onClick={() => handleSorting('name')}
          >
            <div className="fund-screener-table-header-title">Name</div>
            <ArrowSortingIndicator sorting={detectSorting('name')} />
          </div>
        </div>
      ),
      renderCell: (row: FundScreenerDataType) => (
        <div className="fund-screener-table-cell-name-container">
          <div
            className="fund-screener-table-cell-name body-m-regular"
            onMouseEnter={(e) => handleHeaderMouseEnter(e, row.name)}
            onMouseLeave={handleHeaderMouseLeave}
          >
            {row.name}
          </div>
          <div className="fund-screener-table-cell-name-attrs body-s-regular">
            <span
              className="fund-screener-table-cell-code"
              onClick={() => handleFundClick(row.ric)}
            >
              {row.code}
            </span>
            <span className="fund-screener-table-cell-asset-type">
              {' '}
              · {row.assetTypeLabel}
            </span>
          </div>
        </div>
      ),
    };

    const navColumn: ScreenerTableColumnType = {
      id: 'nav',
      accessorKey: 'nav',
      label: 'NAV',
      // min-width always wins over a smaller width, so mobile needs its own
      // override here too, same as Name's `-mobile` modifier.
      cellClassName: classNames('fund-screener-table-nav', {
        'fund-screener-table-nav-mobile': isSmallScreen,
      }),
      headerClassName: classNames('fund-screener-table-header-nav', {
        'fund-screener-table-header-nav-mobile': isSmallScreen,
      }),
      renderHeader: () => sortableHeader('nav', 'NAV', 'right'),
      renderCell: (row: FundScreenerDataType) => (
        <ValueWithCurrency
          value={row.nav}
          currency={row.currency}
          decimals={2}
        />
      ),
    };

    const tnaColumn: ScreenerTableColumnType = {
      id: 'tna',
      accessorKey: 'tna',
      label: 'TNA',
      cellClassName: classNames('fund-screener-table-tna', {
        'fund-screener-table-tna-mobile': isSmallScreen,
      }),
      headerClassName: classNames('fund-screener-table-header-tna', {
        'fund-screener-table-header-tna-mobile': isSmallScreen,
      }),
      renderHeader: () => sortableHeader('tna', 'TNA', 'right'),
      renderCell: (row: FundScreenerDataType) => (
        <ValueWithCurrency
          value={row.tna}
          currency={row.currency}
          decimals={2}
          formatValue={formatMillions}
        />
      ),
    };

    // Drops the "#" index column on mobile to give Name more room.
    return isSmallScreen
      ? [flagColumn, nameColumn, navColumn, tnaColumn]
      : [indexColumn, flagColumn, nameColumn, navColumn, tnaColumn];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, sorting, market, isSmallScreen]);

  // One column per selected criterion, excluding 'nav' (already the basic
  // NAV column — selecting it as a criterion must not duplicate it).
  const dynamicColumns: ScreenerTableColumnType[] = useMemo(() => {
    return selectedCriteria
      .filter((criterion) => criterion.id !== 'nav')
      .map((criterion) => {
        const headerLabel = getCriterionTableHeaderLabel(criterion);
        // Known BE gap: row keys aren't suffixed by period, so e.g. Perf1M
        // and Perf3M can't be told apart by accessorKey yet.
        const accessorKey = criterion.apiField || headerLabel;
        const isScore = criterion.controlType === 'score';
        const isPerformance = criterion.group === FC_GROUPS.performance;
        // fundCurrency/fundCompany are plain filters, not numeric criteria —
        // their value is a string straight from the row.
        const isText =
          criterion.controlType === 'currency' ||
          criterion.controlType === 'search';
        const keyClassName = `fund-screener-table-${criterion.id}`;

        const cellClassName = classNames(
          'fund-screener-table-dynamic',
          keyClassName,
          {
            'fund-screener-table-dynamic-score': isScore,
            'fund-screener-table-dynamic-perf': isPerformance,
          },
        );

        return {
          id: criterion.id,
          accessorKey,
          label: headerLabel,
          cellClassName,
          headerClassName: classNames(
            'fund-screener-table-header-dynamic',
            keyClassName,
          ),
          renderHeader: () =>
            sortableHeader(
              criterion.id,
              headerLabel,
              criterion.id === 'fundCompany' ? 'left' : 'right',
              criterion.label,
            ),
          renderCell: (row: FundScreenerDataType) => {
            const value = row[accessorKey];

            if (isScore) {
              // "N/A" is a distinct rating category, not a generic missing
              // value — gets its own chip instead of "-".
              const isNotRated =
                value === null ||
                value === undefined ||
                value === 'NA' ||
                value === 'N/A';
              return (
                <div className="fund-screener-table-cell-badge-container">
                  {!isNotRated ? (
                    <div className="fund-screener-table-cell-badge body-m-bold">
                      {value}
                    </div>
                  ) : (
                    <div className="fund-screener-table-cell-badge-na body-s-regular">
                      N/A
                    </div>
                  )}
                </div>
              );
            }

            if (isPerformance) {
              return (
                <SingleLineValue
                  className={classNames(
                    'fund-screener-table-cell-perf',
                    'body-m-regular',
                    {
                      positive: (value ?? 0) > 0,
                      negative: (value ?? 0) < 0,
                    },
                  )}
                >
                  {formatPercent(value)}
                </SingleLineValue>
              );
            }

            if (isText) {
              const display =
                value !== null && value !== undefined && value !== ''
                  ? value
                  : '-';
              // Long company names truncate with an ellipsis + hover tooltip.
              if (criterion.id === 'fundCompany') {
                return (
                  <div
                    className="fund-screener-table-cell-value-container fund-screener-table-cell-company-container"
                    onMouseEnter={(e) => handleHeaderMouseEnter(e, display)}
                    onMouseLeave={handleHeaderMouseLeave}
                  >
                    <div className="fund-screener-table-cell-value fund-screener-table-cell-truncate body-m-regular">
                      {display}
                    </div>
                    <div
                      className="fund-screener-table-cell-currency body-s-regular"
                      aria-hidden="true"
                    >
                      &nbsp;
                    </div>
                  </div>
                );
              }
              return (
                <SingleLineValue className="body-m-regular">
                  {display}
                </SingleLineValue>
              );
            }

            return (
              <SingleLineValue className="body-m-regular">
                {value !== null && value !== undefined
                  ? `${formatLocaleNumber(value, 2)}${criterion.unit || ''}`
                  : '-'}
              </SingleLineValue>
            );
          },
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCriteria]);

  return useMemo(
    () => [...basicColumns, ...dynamicColumns],
    [basicColumns, dynamicColumns],
  );
};

export default useFundScreenerColumn;
